import { ShippingCondition, ShippingRule } from '@/application/manage-shipping'
import {
  countRuleChanges,
  ruleStatus,
  savedKey
} from '@/ui/components/rule-list/draft'

// Rules hold values the way useShippingConfig shows them: list values as labels (country names,
// "Bank transfer"), amounts in currency units and booleans as ['True'].

export type FieldKind =
  | 'list'
  | 'tags'
  | 'boolean'
  | 'number'
  | 'money'
  | 'date'

// Groups reuse the edit_shipping.function_names keys the old condition picker used.
export type FieldGroup =
  | 'address'
  | 'compensationAndLogistics'
  | 'orderCharacteristics'
  | 'productCharacteristics'
  | 'value'
  | 'quantity'

export interface FieldSpec {
  group: FieldGroup
  kind: FieldKind
}

// Every ShippingFlatFee function in settings-api's returnamount.DefaultConfiguration but "always",
// which is the "All other returns" row.
export const SHIPPING_FIELDS: Record<string, FieldSpec> = {
  country: { group: 'address', kind: 'list' },
  postalCodePrefix: { group: 'address', kind: 'tags' },
  refundMethod: { group: 'compensationAndLogistics', kind: 'list' },
  returnMethod: { group: 'compensationAndLogistics', kind: 'list' },
  returnReason: { group: 'compensationAndLogistics', kind: 'list' },
  loyalCustomer: { group: 'orderCharacteristics', kind: 'boolean' },
  orderFullyReturned: { group: 'orderCharacteristics', kind: 'boolean' },
  reverExchange: { group: 'orderCharacteristics', kind: 'boolean' },
  orderPurchased: { group: 'orderCharacteristics', kind: 'date' },
  daysSinceBought: { group: 'orderCharacteristics', kind: 'number' },
  orderTagID: { group: 'orderCharacteristics', kind: 'tags' },
  productTagID: { group: 'productCharacteristics', kind: 'tags' },
  productCollectionID: { group: 'productCharacteristics', kind: 'tags' },
  productCategoryID: { group: 'productCharacteristics', kind: 'tags' },
  processProductID: { group: 'productCharacteristics', kind: 'tags' },
  orderTotalAmount: { group: 'value', kind: 'money' },
  totalProductsValue: { group: 'value', kind: 'money' },
  committedOriginalAmount: { group: 'value', kind: 'money' },
  committedExchangeAmount: { group: 'value', kind: 'money' },
  committedGiftAmount: { group: 'value', kind: 'money' },
  committedPromocodeAmount: { group: 'value', kind: 'money' },
  committedBankTransferAmount: { group: 'value', kind: 'money' },
  orderTotalNumberOfProducts: { group: 'quantity', kind: 'number' },
  totalProductsReturned: { group: 'quantity', kind: 'number' },
  // Only reaches a chip when a stored catch-all sits above other rules; the picker never offers it.
  always: { group: 'orderCharacteristics', kind: 'boolean' }
}

export const FIELD_GROUPS: FieldGroup[] = [
  'address',
  'compensationAndLogistics',
  'orderCharacteristics',
  'productCharacteristics',
  'value',
  'quantity'
]

// A function this page doesn't know yet still shows, as free values.
export const fieldSpec = (fn: string): FieldSpec =>
  SHIPPING_FIELDS[fn] ?? { group: 'orderCharacteristics', kind: 'tags' }

export const ALWAYS = 'always'

const isAlways = (rule: ShippingRule) =>
  rule.conditions.length === 1 && rule.conditions[0].function === ALWAYS

export type DraftRule = ShippingRule & { key: string }

// What a return matching no rule pays. Without a stored "always" rule settings-api adds no fee, so
// it is free; `condition` keeps the stored one so an untouched fallback saves back unchanged.
export interface Fallback {
  cost: string
  returnOriginalShippingCosts: boolean
  condition?: ShippingCondition
}

export interface Draft {
  rules: DraftRule[]
  fallback: Fallback
}

// The last rule is the fallback when it always matches; one in the middle stays a rule.
export function splitFallback(rules: ShippingRule[]): {
  rules: ShippingRule[]
  fallback: Fallback
} {
  const last = rules.at(-1)
  if (!last || !isAlways(last))
    return {
      rules,
      fallback: { cost: '0', returnOriginalShippingCosts: false }
    }
  return {
    rules: rules.slice(0, -1),
    fallback: {
      cost: last.params.cost,
      returnOriginalShippingCosts: last.returnOriginalShippingCosts,
      condition: last.conditions[0]
    }
  }
}

export function toDraft(stored: ShippingRule[]): Draft {
  const { rules, fallback } = splitFallback(stored)
  return {
    rules: rules.map((rule, i) => ({ ...rule, key: savedKey(i) })),
    fallback
  }
}

export function fromDraft({ rules, fallback }: Draft): ShippingRule[] {
  const body = rules.map(
    ({ params, returnOriginalShippingCosts, conditions }) => ({
      params,
      returnOriginalShippingCosts,
      conditions
    })
  )
  const charges =
    Number(fallback.cost) !== 0 || fallback.returnOriginalShippingCosts
  if (!fallback.condition && !charges) return body
  return [
    ...body,
    {
      params: { cost: fallback.cost },
      returnOriginalShippingCosts: fallback.returnOriginalShippingCosts,
      conditions: [
        fallback.condition ?? { function: ALWAYS, comparison: 'Is', values: [] }
      ]
    }
  ]
}

// Equal rules may list conditions and values in any order, or write a cost as 5 or 5.00: the
// assistant reorders them freely.
export function ruleSignature(rule: ShippingRule): string {
  const cost = Number(rule.params.cost)
  return JSON.stringify({
    cost: isNaN(cost) ? rule.params.cost : cost,
    returnOriginalShippingCosts: rule.returnOriginalShippingCosts,
    conditions: rule.conditions
      .map((c) =>
        JSON.stringify([c.function, c.comparison, [...c.values].sort()])
      )
      .sort()
  })
}

const sameRule = (a: ShippingRule, b: ShippingRule | undefined) =>
  !!b && ruleSignature(a) === ruleSignature(b)

// Rules the assistant sent back keep the key of the stored rule they equal, else of the stored
// rule at their position, so an edited rule reads as changed rather than new.
export function draftFromRules(
  rules: ShippingRule[],
  stored: ShippingRule[]
): Draft {
  const next = splitFallback(rules)
  const saved = splitFallback(stored).rules
  const claimed = new Set<number>()
  const claim = (i: number) => {
    claimed.add(i)
    return savedKey(i)
  }
  const stamp = Date.now()
  // A stored rule the reply still holds unchanged is claimed by that copy, never by position.
  const unchanged = (k: number) => next.rules.some((r) => sameRule(r, saved[k]))
  return {
    fallback: next.fallback,
    rules: next.rules.map((rule, i) => {
      const same = saved.findIndex(
        (s, k) => !claimed.has(k) && sameRule(rule, s)
      )
      if (same >= 0) return { ...rule, key: claim(same) }
      if (i < saved.length && !claimed.has(i) && !unchanged(i))
        return { ...rule, key: claim(i) }
      return { ...rule, key: `new-${stamp}-${i}` }
    })
  }
}

const NO_FALLBACK: Fallback = { cost: '0', returnOriginalShippingCosts: false }

export const statusOf = (rule: DraftRule, stored: ShippingRule[]) =>
  ruleStatus(rule, splitFallback(stored).rules, (r, original) =>
    sameRule(fromDraft({ rules: [r], fallback: NO_FALLBACK })[0], original)
  )

export function countChanges(draft: Draft, stored: ShippingRule[]) {
  const saved = splitFallback(stored)
  const fallbackChanged =
    Number(draft.fallback.cost) !== Number(saved.fallback.cost) ||
    draft.fallback.returnOriginalShippingCosts !==
      saved.fallback.returnOriginalShippingCosts
  return (
    countRuleChanges(draft.rules, saved.rules.length, (rule) =>
      statusOf(rule, stored)
    ) + (fallbackChanged ? 1 : 0)
  )
}

export const isValidCost = (cost: string) =>
  cost.trim() !== '' && !isNaN(Number(cost)) && Number(cost) >= 0

export function isComplete(condition: ShippingCondition) {
  if (!condition.function || !condition.comparison) return false
  const { kind } = fieldSpec(condition.function)
  if (kind === 'boolean') return true
  const values = condition.values.filter((v) => v.trim() !== '')
  if (values.length === 0) return false
  return kind === 'number' || kind === 'money'
    ? !isNaN(Number(values[0]))
    : true
}

// A rule without conditions would match every return and shadow the rules below it: that is
// what "All other returns" is for.
export const isRuleComplete = (rule: ShippingRule) =>
  rule.conditions.length > 0 &&
  rule.conditions.every(isComplete) &&
  isValidCost(rule.params.cost)

// Rules saved as they are don't block a save: older data can hold values this page can't read
// (e.g. a blank return reason), and the merchant may be changing something else.
export const countIncomplete = (draft: Draft, stored: ShippingRule[]) =>
  draft.rules.filter(
    (rule) => !isRuleComplete(rule) && statusOf(rule, stored) !== 'saved'
  ).length + (isValidCost(draft.fallback.cost) ? 0 : 1)

export const newCondition = (
  fn: string,
  comparisons: string[]
): ShippingCondition => ({
  function: fn,
  comparison: comparisons[0] ?? '',
  values: fieldSpec(fn).kind === 'boolean' ? ['True'] : []
})

// A test return: list fields hold the label a rule would hold, the order value currency units.
export interface TestReturn {
  country: string
  refundMethod: string
  returnMethod: string
  orderTotalAmount: string
}

// "unknown" when a rule asks about something the test doesn't: the test skips it rather than guess.
export type Outcome = 'match' | 'miss' | 'unknown'

const COMPARE: Record<string, (a: number, b: number) => boolean> = {
  GreaterThan: (a, b) => a > b,
  GreaterThanOrEqual: (a, b) => a >= b,
  LessThan: (a, b) => a < b,
  LessThanOrEqual: (a, b) => a <= b,
  Equal: (a, b) => a === b,
  NotEqual: (a, b) => a !== b
}

function conditionOutcome(
  condition: ShippingCondition,
  test: TestReturn,
  inGroup: (country: string, value: string) => boolean
): Outcome {
  switch (condition.function) {
    case 'country':
    case 'refundMethod':
    case 'returnMethod': {
      const fact = test[condition.function]
      if (!fact) return 'unknown'
      const hit = condition.values.some(
        (value) =>
          value === fact ||
          (condition.function === 'country' && inGroup(fact, value))
      )
      if (condition.comparison === 'In') return hit ? 'match' : 'miss'
      if (condition.comparison === 'NotIn') return hit ? 'miss' : 'match'
      return 'unknown'
    }
    case 'orderTotalAmount': {
      const compare = COMPARE[condition.comparison]
      const fact = Number(test.orderTotalAmount)
      if (!compare || test.orderTotalAmount.trim() === '' || isNaN(fact))
        return 'unknown'
      return compare(fact, Number(condition.values[0])) ? 'match' : 'miss'
    }
    default:
      return 'unknown'
  }
}

// A rule misses as soon as one condition misses, even if others can't be checked.
export function ruleOutcome(
  rule: ShippingRule,
  test: TestReturn,
  inGroup: (country: string, value: string) => boolean
): Outcome {
  if (rule.conditions.length === 0) return 'miss'
  const outcomes = rule.conditions.map((c) =>
    conditionOutcome(c, test, inGroup)
  )
  if (outcomes.includes('miss')) return 'miss'
  return outcomes.includes('unknown') ? 'unknown' : 'match'
}

// The first rule that matches, or -1 for "All other returns"; skipped are the rules above it the
// test couldn't decide.
export function runTest(
  rules: ShippingRule[],
  test: TestReturn,
  inGroup: (country: string, value: string) => boolean
): { matched: number; skipped: number[] } {
  const outcomes = rules.map((rule) => ruleOutcome(rule, test, inGroup))
  const matched = outcomes.indexOf('match')
  const above = matched < 0 ? outcomes : outcomes.slice(0, matched)
  const skipped = above.flatMap((o, i) => (o === 'unknown' ? [i] : []))
  return { matched, skipped }
}
